/**
 * Inline row inside the publish dialog's share-link card.
 *
 * Two states:
 *   - Off (default): one-line toggle "Allow reviewers to comment"
 *   - On: toggle stays on; a "Copy review link" button appears below
 *
 * The review link is just the share URL with `?review=true` appended — same token,
 * different mode. Server-side gating (allowReviews flag) is what actually makes
 * comments work; the button just sets the flag in the URL for convenience.
 */

import { Check, Copy, MessageSquare } from 'lucide-react';
import type { VideoShareLinkInfo } from '@/services/api';
import { cn } from '@/lib/utils';

interface Props {
  link: VideoShareLinkInfo;
  onToggle: (next: boolean) => void;
  onCopyReviewLink: () => void;
  copied: boolean;
}

export function ReviewerAccessRow({ link, onToggle, onCopyReviewLink, copied }: Props) {
  const enabled = !!link.allowReviews;

  return (
    <div
      className={cn(
        'mb-1.5 px-2 py-1.5 rounded border text-[11px] transition-colors',
        enabled
          ? 'bg-amber-500/5 border-amber-500/20'
          : 'bg-muted/30 border-border/50',
      )}
    >
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-0.5 h-3 w-3 cursor-pointer accent-amber-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3 text-amber-600 dark:text-amber-400" />
            <span className="font-medium text-foreground">Allow reviewers to comment</span>
          </div>
          <p className="text-muted-foreground mt-0.5 leading-snug">
            {enabled
              ? 'Reviewers can leave timestamped comments on this video.'
              : 'When off, anyone with the link can only watch — no comments allowed.'}
          </p>
        </div>
      </label>

      {enabled && (
        <div className="mt-2 flex items-center gap-1.5 pl-5">
          <code className="flex-1 bg-background/60 rounded px-2 py-1 text-[10px] truncate font-mono text-muted-foreground">
            {link.shareUrl}?review=true
          </code>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCopyReviewLink();
            }}
            className="h-6 px-2 rounded text-[10px] font-medium inline-flex items-center gap-1 bg-amber-500 text-amber-950 hover:bg-amber-400 transition-colors"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copied' : 'Copy review link'}
          </button>
        </div>
      )}
    </div>
  );
}
